/**
 * IBKR (Interactive Brokers) integration types for the Covered Calls Tracker.
 */

/** Connection status for a tenant's IBKR integration. */
export enum ConnectionStatus {
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  AUTH_FAILED = 'AUTH_FAILED',
}

/** IBKR connection configuration stored in DynamoDB. */
export interface IbkrConnectionConfig {
  tenantId: string;
  gatewayUrl: string; // ngrok URL to the local Client Portal Gateway
  enabled: boolean; // controls whether the automated 5-min fetcher runs
  status: ConnectionStatus;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

/** Input DTO for creating/updating IBKR connection settings. */
export interface IbkrConnectionInput {
  gatewayUrl: string;
  enabled: boolean;
}

/** Response DTO for IBKR connection. */
export interface IbkrConnectionResponse {
  gatewayUrl: string;
  enabled: boolean;
  status: ConnectionStatus;
}

/**
 * Specific reason behind a non-connected IBKR live status, so the UI can give
 * the user targeted remediation guidance.
 * - GATEWAY_DOWN: ngrok tunnel is up but the IB Gateway upstream isn't running
 *   (ngrok returns 502 / ERR_NGROK_8012).
 * - TUNNEL_UNREACHABLE: the gateway URL couldn't be reached at all (ngrok not
 *   running, wrong URL, or DNS/network failure).
 * - TIMEOUT: the request timed out.
 * - SESSION_EXPIRED: reachable and running, but the IBKR session needs re-auth.
 * - NOT_CONFIGURED: no gateway URL has been saved yet.
 * - GATEWAY_ERROR: reachable but returned an unexpected HTTP error.
 */
export type IbkrStatusReason =
  | 'GATEWAY_DOWN'
  | 'TUNNEL_UNREACHABLE'
  | 'TIMEOUT'
  | 'SESSION_EXPIRED'
  | 'NOT_CONFIGURED'
  | 'GATEWAY_ERROR';

/** Response DTO for the live connection check (GET /api/settings/ibkr/status). */
export interface IbkrLiveStatusResponse {
  status: ConnectionStatus;
  checkedAt: string;
  message?: string;
  /** Machine-readable cause for a non-connected status (undefined when connected). */
  reason?: IbkrStatusReason;
}


/** Market schedule definition. */
export interface MarketSchedule {
  exchange: string;
  timezone: string;
  openTime: string; // HH:mm
  closeTime: string; // HH:mm
  weekdays: number[]; // 1=Mon, 5=Fri
}
